import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Card, ErrorNote, Skeleton } from './Home.js';

/**
 * The Activity timeline (docs/SCREEN-SPECS.md §9, SECURITY-ARCHITECTURE.md §5).
 *
 * This is the audit trail as a PRODUCT feature, not an admin console. Every
 * reveal of a PAN, every export, every erasure is visible to the whole family in
 * their own app — which is the strongest available deterrent against insider
 * misuse, because the other spouse *will* see it. That is why it lives in the
 * main navigation rather than behind a settings screen.
 *
 * Summaries are rendered server-side and stored verbatim, so an event stays
 * readable after its subject is erased. This screen must not reconstruct
 * sentences from `data` — doing so would make the timeline lie the moment the
 * thing it refers to is gone.
 */

interface ActivityEvent {
  id: string;
  at: string;
  kind: string;
  actorType: 'user' | 'system';
  actorId: string | null;
  subjectType: string | null;
  subjectId: string | null;
  summary: string;
  data: Record<string, unknown> | null;
}

/**
 * Security-relevant kinds are marked. Colour is never the only signal — each
 * carries a glyph too (DESIGN-SYSTEM §2.2: value colours never appear alone).
 */
const NOTABLE: Record<string, { glyph: string; tone: string; label: string }> = {
  pii_reveal: { glyph: '◎', tone: 'var(--color-status-warning)', label: 'identity revealed' },
  member_erased: { glyph: '⊘', tone: 'var(--color-status-critical)', label: 'erased' },
  export_created: { glyph: '↧', tone: 'var(--color-status-warning)', label: 'export' },
  export_downloaded: { glyph: '↧', tone: 'var(--color-status-warning)', label: 'export' },
  login_failed: { glyph: '⚠', tone: 'var(--color-status-warning)', label: 'failed sign-in' },
  logout_all: { glyph: '⚠', tone: 'var(--color-status-warning)', label: 'signed out everywhere' },
  purge_executed: { glyph: '⊘', tone: 'var(--color-status-critical)', label: 'purge' },
  key_rotated: { glyph: '⚿', tone: 'var(--color-status-warning)', label: 'key rotated' },
};

/** Group by calendar day so the timeline reads as a diary, not a log file. */
function dayLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (isSameDay(date, today)) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

export function Activity() {
  const { data, isPending, error } = useQuery({
    queryKey: ['activity'],
    queryFn: () => api.get<ActivityEvent[]>('/activity?limit=100'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const days = new Map<string, ActivityEvent[]>();
  for (const event of data) {
    const key = dayLabel(event.at);
    if (!days.has(key)) days.set(key, []);
    days.get(key)!.push(event);
  }

  return (
    <div>
      <h1 className="page-title">Activity</h1>
      <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)', margin: '0 0 var(--space-5)' }}>
        Everything the app did, and everything anyone did with your data.
      </p>

      {data.length === 0 && (
        <Card title="Nothing yet">
          <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)', margin: 0 }}>
            Sign-ins, statement uploads and identity reveals will appear here.
          </p>
        </Card>
      )}

      {[...days.entries()].map(([day, events]) => (
        <Card key={day} title={day}>
          {events.map((event) => {
            const notable = NOTABLE[event.kind];
            return (
              <article
                key={event.id}
                style={{
                  display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-2) 0',
                  borderBottom: '1px solid var(--color-separator)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ color: notable?.tone ?? 'var(--color-label-tertiary)', font: 'var(--type-subhead)', lineHeight: 1.4 }}
                >
                  {notable?.glyph ?? '·'}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', font: 'var(--type-subhead)' }}>{event.summary}</span>
                  <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                    {new Date(event.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    {event.actorType === 'system' && ' · automatic'}
                    {/* The label repeats the colour in words, for anyone who
                        cannot distinguish it — and for everyone in bright sun. */}
                    {notable && ` · ${notable.label}`}
                  </span>
                </span>
              </article>
            );
          })}
        </Card>
      ))}
    </div>
  );
}
